import { useMemo, type ComponentProps, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import useLuxPlusDisplayedMode from 'luxPlus/hooks/useLuxPlusDisplayedMode'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'
import { mediaQueryUp } from 'components/utils/breakpoint'

const InlinePriceRow = styled.span<{ $align: 'start' | 'end', $desktopAlign: 'start' | 'end' }>`
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  column-gap: 0.25em;
  justify-content: ${props => props.$align === 'end' ? 'flex-end' : 'flex-start'};

  ${mediaQueryUp.desktop} {
    justify-content: ${props => props.$desktopAlign === 'end' ? 'flex-end' : 'flex-start'};
  }
`

/**
 * Displays a loyalty points burn price, showing how many points the user will spend
 * alongside any remaining cash balance. Handles both standard and LuxPlus member pricing.
 */
interface Props {
  /** The full cash price of the offer before any points are applied */
  totalPrice: number
  /** Cash-only extras, added after hotel redemption and never reduced by hotel points. */
  nonRedeemableCashPrice?: number
  /** Payments already allocated across a booking with different per-item conversion rates. */
  allocatedPayment?: {
    regular: { points: number, cash: number }
    member: { points: number, cash: number }
  }
  /** The number of points the user has chosen to burn */
  pointsToBurn?: number
  /** The full points value of the offer (i.e. the price expressed entirely in points) */
  priceInPoints?: number
  size?: 'S' | 'M' | 'L'
  /** Sale unit label, e.g. "total", "/person", "/transfer" — same values as PriceRowPrice */
  saleUnit?: string
  memberPrice?: number
  /** When true, hides the cash component if it would be zero (points cover the full price) */
  hideZeroCash?: boolean
  /** Points value of the offer at the member cash price (when it differs from priceInPoints) */
  memberPriceInPoints?: number
  /** Show the full offer price as points only (no cash component) */
  pointsOnly?: boolean
  offerConfig?: App.OfferLuxPlusConfig
  isTour?: boolean
  mode?: App.LuxPlusPriceStackMode
  /** When true, renders the points amount and cash amount on separate rows instead of one inline row */
  stacked?: boolean
  /** Horizontal alignment of the price block and its lines (e.g. the "Non-member" caption); @default 'start' */
  horizontalAlign?: 'start' | 'end'
  /** Horizontal alignment from the desktop breakpoint up; defaults to `horizontalAlign` */
  desktopHorizontalAlign?: 'start' | 'end'
}

const BURN_POINTS_HEADING_VARIANT: Record<NonNullable<Props['size']>, ComponentProps<typeof Heading>['variant']> = {
  S: 'heading6',
  M: 'heading5',
  L: 'heading4',
}

interface BurnPointsAmountProps {
  points: number
  size: NonNullable<Props['size']>
  colour?: ComponentProps<typeof Heading>['colour']
}

/** Matches RewardBundlePriceBlock — points number and abbreviated unit at heading size */
function BurnPointsAmount({ points, size, colour }: BurnPointsAmountProps) {
  return (
    <Heading variant={BURN_POINTS_HEADING_VARIANT[size]} as="span" colour={colour}>
      <FormatLoyaltyPoints points={points} abbreviated />
    </Heading>
  )
}

function LuxPlusSaleUnitCaption({ saleUnitLabel }: { saleUnitLabel: ReactNode }) {
  const hasSaleUnitLabel = typeof saleUnitLabel === 'string' ? saleUnitLabel.length > 0 : !!saleUnitLabel

  return (
    <Caption variant="medium" as="span" colour="lux-plus" weight="normal" format="lowercase">
      {hasSaleUnitLabel ? (
        <FormattedMessage
          id="Bqt2LSkQYirkR4WS1QYh"
          defaultMessage="{saleUnit} with {luxPlusLogo}"
          description="Sale unit label with LuxPlus branding for points price"
          values={{
            saleUnit: saleUnitLabel,
            luxPlusLogo: <LuxPlusLogoWrapper height={8} />,
          }}
        />
      ) : (
        <FormattedMessage
          id="qg18mjM_yahXRtEZVoVD"
          defaultMessage="with <luxPlusLogo></luxPlusLogo>"
          description="Caption suffix appended after a LuxPlus member price that reads with the LuxPlus logo"
          values={{
            luxPlusLogo: () => <LuxPlusLogoWrapper height={8} />,
          }}
        />
      )}
    </Caption>
  )
}

function LoyaltyBurnPriceDisplay(props: Props) {
  const {
    totalPrice,
    nonRedeemableCashPrice = 0,
    allocatedPayment,
    pointsToBurn = 0,
    priceInPoints = 0,
    size = 'L',
    saleUnit = 'total',
    memberPrice,
    hideZeroCash = false,
    memberPriceInPoints,
    pointsOnly = false,
    offerConfig,
    isTour,
    mode,
    stacked = false,
    horizontalAlign = 'start',
    desktopHorizontalAlign = horizontalAlign,
  } = props

  const intl = useIntl()
  const displayedMode = useLuxPlusDisplayedMode({
    price: totalPrice,
    memberPrice: memberPrice ?? 0,
    offerConfig,
    isTour,
    mode,
  })

  const resolvedMemberPrice = memberPrice && memberPrice > 0 ? memberPrice : totalPrice
  const resolvedMemberPriceInPoints = memberPriceInPoints ?? priceInPoints
  const pointsCashValue = priceInPoints > 0 ? (pointsToBurn * totalPrice) / priceInPoints : 0
  const remainingCash = allocatedPayment?.regular.cash ?? (nonRedeemableCashPrice + (pointsToBurn >= priceInPoints ? 0 : Math.ceil(Math.max(0, totalPrice - pointsCashValue))))
  const memberPointsCashValue = resolvedMemberPriceInPoints > 0
    ? (pointsToBurn * resolvedMemberPrice) / resolvedMemberPriceInPoints
    : 0
  const memberRemainingCash = allocatedPayment?.member.cash ?? (nonRedeemableCashPrice + (pointsToBurn >= resolvedMemberPriceInPoints
    ? 0
    : Math.ceil(Math.max(0, resolvedMemberPrice - memberPointsCashValue))))
  const effectivePointsBurned = allocatedPayment?.regular.points ?? Math.min(pointsToBurn, priceInPoints)
  const memberPointsDisplay = pointsOnly
    ? resolvedMemberPriceInPoints
    : allocatedPayment?.member.points ?? Math.min(pointsToBurn, resolvedMemberPriceInPoints)

  const saleUnitLabel = formatSaleUnitLabel(intl, saleUnit, { withLeadingSlash: true })

  const showCash = remainingCash > 0
  const showMemberCash = memberRemainingCash > 0

  const nonMemberPointsDisplay = pointsOnly ? priceInPoints : effectivePointsBurned

  const nonMemberBurnPriceLabel = useMemo(() => {
    if (pointsOnly || (hideZeroCash && !showCash)) {
      return intl.formatMessage(
        {
          id: 'lRU1m5sm7wOxouX_aYnJ',
          defaultMessage: 'Non-member {points}',
          description: 'Price caption label for non-member points-only display',
        },
        {
          points: <b><FormatLoyaltyPoints points={nonMemberPointsDisplay} abbreviated /></b>,
        },
      )
    }
    return intl.formatMessage(
      {
        id: 'PIwh0lBFZFNGTPKTnNh_',
        defaultMessage: 'Non-member {points} + {price}',
        description: 'Price caption label for non-member points + price display',
      },
      {
        points: <b><FormatLoyaltyPoints points={effectivePointsBurned} abbreviated /></b>,
        price: <b><FormatCurrency value={remainingCash} format="roundedDollar" /></b>,
      },
    )
  }, [intl, effectivePointsBurned, nonMemberPointsDisplay, remainingCash, showCash, hideZeroCash, pointsOnly])

  if (!allocatedPayment && (props.pointsToBurn === undefined || props.priceInPoints === undefined)) return null

  const headingVariant = BURN_POINTS_HEADING_VARIANT[size]

  const leadWithMemberPrice = displayedMode === 'member-prominent' || displayedMode === 'regular-prominent'
  const isMemberOnly = displayedMode === 'member-only' || displayedMode === 'member-checkout-total' || displayedMode === 'member-compact'

  if (pointsOnly && (leadWithMemberPrice || isMemberOnly)) {
    return (
      <Group direction="vertical" gap={2} horizontalAlign={horizontalAlign} desktopHorizontalAlign={desktopHorizontalAlign} data-testid="burn-price-display-luxplus-points-only">
        <InlinePriceRow $align={horizontalAlign} $desktopAlign={desktopHorizontalAlign}>
          <BurnPointsAmount points={memberPointsDisplay} size={size} colour="lux-plus" />
          <LuxPlusSaleUnitCaption saleUnitLabel={saleUnitLabel} />
        </InlinePriceRow>
        {leadWithMemberPrice && resolvedMemberPrice < totalPrice && <Caption variant="medium" colour="light-foreground-alt">
          {nonMemberBurnPriceLabel}
        </Caption>}
      </Group>
    )
  }

  if (pointsOnly) {
    return (
      <Group direction="vertical" gap={2} horizontalAlign={horizontalAlign} desktopHorizontalAlign={desktopHorizontalAlign}>
        <InlinePriceRow $align={horizontalAlign} $desktopAlign={desktopHorizontalAlign} data-testid="burn-price-display-points-only">
          <BurnPointsAmount points={priceInPoints} size={size} colour="normal-contrast" />
          <Caption variant="medium" as="span" colour="light-foreground-alt" weight="normal">
            {saleUnitLabel}
          </Caption>
        </InlinePriceRow>
      </Group>
    )
  }

  if (leadWithMemberPrice || isMemberOnly) {
    return (
      <Group direction="vertical" gap={2} horizontalAlign={horizontalAlign} desktopHorizontalAlign={desktopHorizontalAlign} data-testid="burn-price-display-luxplus">
        <InlinePriceRow $align={horizontalAlign} $desktopAlign={desktopHorizontalAlign}>
          <BurnPointsAmount points={memberPointsDisplay} size={size} colour="lux-plus" />
          {showMemberCash && (
            <Heading variant={headingVariant} as="span" colour="lux-plus">
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              {' + '}
              <FormatCurrency value={memberRemainingCash} format="roundedDollar" />
            </Heading>
          )}
          <LuxPlusSaleUnitCaption saleUnitLabel={saleUnitLabel} />
        </InlinePriceRow>
        {leadWithMemberPrice && resolvedMemberPrice < totalPrice && <Caption variant="medium" colour="light-foreground-alt">
          {nonMemberBurnPriceLabel}
        </Caption>}
      </Group>
    )
  }

  if (stacked) {
    return (
      <Group direction="vertical" gap={2} horizontalAlign={horizontalAlign} desktopHorizontalAlign={desktopHorizontalAlign} data-testid="burn-price-display">
        <BurnPointsAmount points={effectivePointsBurned} size={size} colour="normal-contrast" />
        <InlinePriceRow $align={horizontalAlign} $desktopAlign={desktopHorizontalAlign}>
          {showCash && (
            <Heading variant={headingVariant} as="span">
              {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
              {' + '}
              <FormatCurrency value={remainingCash} format="roundedDollar" />
            </Heading>
          )}
          <Caption variant="medium" as="span" colour="light-foreground-alt" weight="normal">
            {saleUnitLabel}
          </Caption>
        </InlinePriceRow>
      </Group>
    )
  }

  return (
    <Group direction="vertical" gap={2} horizontalAlign={horizontalAlign} desktopHorizontalAlign={desktopHorizontalAlign}>
      <InlinePriceRow $align={horizontalAlign} $desktopAlign={desktopHorizontalAlign} data-testid="burn-price-display">
        <BurnPointsAmount points={effectivePointsBurned} size={size} colour="normal-contrast" />
        {showCash && (
          <Heading variant={headingVariant} as="span">
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            {' + '}
            <FormatCurrency value={remainingCash} format="roundedDollar" />
          </Heading>
        )}
        <Caption variant="medium" as="span" colour="light-foreground-alt" weight="normal">
          {saleUnitLabel}
        </Caption>
      </InlinePriceRow>
    </Group>
  )
}

export default LoyaltyBurnPriceDisplay
